import { gql } from '@apollo/client';
import { Delta } from 'quill/core';
import React from 'react';
import EmptyState from '../components/EmptyState';
import type { FetchMoreHandler } from '../components/InfiniteScrollWaypoint';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import type { SearchViewSlabResults_ResultsFragment } from '../graphql/generated/graphqlTypes';
import GroupDetailCard from '../groups/GroupDetailCard';
import TopicCard from '../topic/TopicCard';
import UserCard from '../user/UserCard';
import CommentResultCard from './CommentResultCard';
import PostResultCard from './PostResultCard';

const FRAGMENT = gql`
  fragment SearchViewSlabResults_results on SearchResult {
    ... on PostSearchResult {
      content
      highlight
      highlightIndex
      title
      post {
        ...PostResultCard_post
      }
      ancestors {
        id
        ...PostResultCard_ancestors
      }
      resultId
    }
    ... on TopicSearchResult {
      name
      description
      topic {
        ...TopicCard_topic
      }
      ancestors {
        id
        ...TopicCard_ancestors
      }
    }
    ... on UserSearchResult {
      description
      name
      title
      user {
        ...UserCard_user
      }
    }
    ... on CommentSearchResult {
      content
      comment {
        id
        ...CommentResultCard_comment
      }
    }
    ... on GroupSearchResult {
      name
      description
      group {
        id
        ...GroupDetailCard_group
      }
    }
  }
  ${PostResultCard.fragments.post}
  ${PostResultCard.fragments.ancestors}
  ${TopicCard.fragments.topic}
  ${TopicCard.fragments.ancestors}
  ${UserCard.fragments.user}
  ${CommentResultCard.fragments.comment}
  ${GroupDetailCard.fragments.group}
`;

interface SearchViewSlabResultsProps {
  data: SearchViewSlabResults_ResultsFragment[];
  loading: boolean;
  fetchMore?: FetchMoreHandler | null;
  queryId?: string;
}

const SearchViewSlabResults = ({
  data,
  loading,
  fetchMore,
  queryId,
}: SearchViewSlabResultsProps) => {
  if (!data.length) {
    return loading ? null : (
      <EmptyState
        imageName="notFound"
        title="No results found"
        message="Try another search with fewer keywords"
      />
    );
  }

  return (
    <>
      <div className="search-result-cards">
        {data.map((result, index) => {
          if ('post' in result) {
            const {
              highlight,
              highlightIndex,
              post,
              title,
              ancestors,
              resultId,
            } = result;

            const highlightDelta = new Delta(highlight);
            const content = new Delta(result.content).compose(highlightDelta);

            return (
              <PostResultCard
                key={post.id}
                post={post}
                title={title}
                content={content}
                queryId={queryId}
                resultId={resultId ?? undefined}
                position={index + 1}
                highlight={highlightDelta}
                highlightIndex={highlightIndex || undefined}
                ancestors={ancestors}
              />
            );
          }
          if ('topic' in result) {
            const { topic, name, description, ancestors } = result;

            return (
              <TopicCard
                key={result.topic.id}
                topic={topic}
                name={name}
                description={description}
                ancestors={ancestors}
              />
            );
          }
          if ('user' in result) {
            const { name, title, description, user } = result;

            return (
              <UserCard
                key={result.user.id}
                user={user}
                name={name}
                title={title}
                description={description}
                large
              />
            );
          }

          if ('comment' in result) {
            return (
              <CommentResultCard
                key={result.comment.id}
                comment={result.comment}
                content={result.content}
              />
            );
          }

          if ('group' in result) {
            const { name, group, description } = result;

            return (
              <GroupDetailCard
                key={group.id}
                group={{
                  ...group,
                  name: name ?? group.name,
                  description: description ?? group.description,
                }}
                numLines={10}
              />
            );
          }
          return null;
        })}
      </div>
      <InfiniteScrollWaypoint fetchMore={fetchMore} />
    </>
  );
};

SearchViewSlabResults.fragments = {
  results: FRAGMENT,
};

export default SearchViewSlabResults;
